import React, { useContext, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch, RootState } from "store/store";
import { canvasDimensions, orgIdsWithRescaleImage } from "config/constant";
import { MediaContext } from "context/MediaContext";
import { enqueueItem } from "store/cameraGrabSlice";
import { getCameraGrabBlobPayload } from "helpers/blob-payload";
import { parseOrgIds } from "helpers/common";
import { setUiErrorScreen } from "store/app.slice";
import { ClientErrorTypes, ErrorTypes } from "globals/enums";
import { handleError } from "helpers/sentry";
import { detectMob } from "helpers/browser-helper";

const useCameraGrab = () => {
  const { videoStream, setMediaStreamError } = useContext(MediaContext);
  const storage = useSelector((state: RootState) => state.app.storage);
  const session = useSelector((state: RootState) => state.app.session);
  const isCameraGrabEnabled = useSelector((state: RootState) => state.app.isCameraGrabEnabled);
  const organizationId = useSelector((state: RootState) => state.app.organizationId);
  const dispatch = useAppDispatch();
  const cameraGrabTimer = useRef<NodeJS.Timeout | null>(undefined);
  const storageRef = useRef(storage);

  useEffect(() => {
    storageRef.current = storage;
  }, [storage]);

  const grabCamera = () => {
    const videoElement = document.getElementById("recorder_video_custom") as HTMLVideoElement;
    if (videoElement) {
      const isMobile = detectMob();
      // created different canvas for camera grabs so that it does not conflict with face detection rectangle
      const canvas = document.createElement("canvas");
      const { canvasWidth, canvasHeight } = canvasDimensions;
      const videoWidth = videoElement.videoWidth;
      const videoHeight = videoElement.videoHeight;

      // For mobile, upload exact resolution received from the device.
      if (isMobile && videoWidth > 0 && videoHeight > 0) {
        canvas.width = videoWidth;
        canvas.height = videoHeight;
      } else {
        canvas.height = canvasHeight;
        canvas.width = canvasWidth;
      }
      const context = canvas.getContext("2d");
      
      // Calculate dimensions to maintain aspect ratio
      let drawWidth = canvas.width;
      let drawHeight = canvas.height;
      let offsetX = 0;
      let offsetY = 0;
      
      if (context) {
        if (isMobile && videoWidth > 0 && videoHeight > 0) {
          // Exact resolution capture for mobile devices.
          context.drawImage(videoElement, 0, 0, videoWidth, videoHeight);
        } else {
          // Calculate the scaling factor to fit the video in the canvas while preserving aspect ratio
          const videoRatio = videoWidth / videoHeight;
          const canvasRatio = canvas.width / canvas.height;

          if (videoRatio > canvasRatio) {
            // Video is wider than canvas (relative to height)
            drawHeight = canvas.width / videoRatio;
            offsetY = (canvas.height - drawHeight) / 2;
          } else {
            // Video is taller than canvas (relative to width)
            drawWidth = canvas.height * videoRatio;
            offsetX = (canvas.width - drawWidth) / 2;
          }

          // Fill with black background
          context.fillStyle = "#000000";
          context.fillRect(0, 0, canvas.width, canvas.height);

          // Draw the video with proper aspect ratio
          const shouldRescale = parseOrgIds(orgIdsWithRescaleImage).includes(organizationId);
          if (shouldRescale) {
            // Apply rescaling logic
            context.drawImage(videoElement, offsetX, offsetY, drawWidth, drawHeight);
          } else {
            // Draw without rescaling
            context.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
          }
        }
      }
      canvas.toBlob(async (blob) => {
        dispatch(enqueueItem(getCameraGrabBlobPayload(storageRef.current, session, blob)));
      }, "image/png");
    }
  };

  React.useEffect(() => {
    if (isCameraGrabEnabled && videoStream && videoStream.active) {
      cameraGrabTimer.current = setInterval(() => {
        grabCamera();
      }, 10000);
    } else if (isCameraGrabEnabled) {
      const error = new DOMException("Track ended", "NotAllowedError");
      setMediaStreamError(error);
      handleError(error, ClientErrorTypes.CameraGrabTrackEndedError);
      dispatch(setUiErrorScreen({ uiErrorScreen: ErrorTypes.MediaStreamError }));
      clearInterval(cameraGrabTimer.current!);
    } else {
      clearInterval(cameraGrabTimer.current!);
    }
  }, [isCameraGrabEnabled, videoStream]);

  return [isCameraGrabEnabled];
};

export default useCameraGrab;
